import { useEffect, useState } from "react";
import { Download, Share, SquarePlus, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

let deferred = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferred = e; window.dispatchEvent(new Event("pwa-ready")); });
}
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3"><span className="install-step">{n}</span><span className="text-sm text-slate-700 leading-snug pt-0.5">{children}{Icon && <Icon className="inline h-4 w-4 mx-1 text-[#0072C6] align-[-2px]" />}</span></li>
);

const Help = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm" data-testid="install-help-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><img src="/icon-192.png" alt="" className="h-9 w-9 rounded-lg border border-slate-200" /> Install the Experior app</DialogTitle>
        <DialogDescription>Add the portal to your home screen. It opens full screen like an app.</DialogDescription>
      </DialogHeader>
      {isIOS() ? (
        <ol className="space-y-3" data-testid="install-steps-ios">
          <Step n={1}>Open this page in <b>Safari</b></Step>
          <Step n={2} icon={Share}>Tap the <b>Share</b> button</Step>
          <Step n={3} icon={SquarePlus}>Choose <b>Add to Home Screen</b>, then tap <b>Add</b></Step>
        </ol>
      ) : (
        <ol className="space-y-3" data-testid="install-steps-android">
          <Step n={1}>Open this page in <b>Chrome</b></Step>
          <Step n={2} icon={MoreVertical}>Tap the <b>menu</b> (three dots, top right)</Step>
          <Step n={3}>Choose <b>Install app</b> or <b>Add to Home screen</b></Step>
        </ol>
      )}
    </DialogContent>
  </Dialog>
);

export const InstallAppButton = ({ className = "", label = "Install app" }) => {
  const [ready, setReady] = useState(!!deferred);
  const [help, setHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const r = () => setReady(true);
    const done = () => setInstalled(true);
    window.addEventListener("pwa-ready", r);
    window.addEventListener("appinstalled", done);
    return () => { window.removeEventListener("pwa-ready", r); window.removeEventListener("appinstalled", done); };
  }, []);
  if (installed) return null;
  const install = async () => {
    if (!ready || !deferred) return setHelp(true);
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    deferred = null; setReady(false);
    if (outcome === "accepted") setInstalled(true);
  };
  return (<>
    <Button variant="outline" size="sm" onClick={install} data-testid="install-app-button" className={`gap-1.5 ${className}`}><Download className="h-4 w-4" /><span>{label}</span></Button>
    <Help open={help} onOpenChange={setHelp} />
  </>);
};
